import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import { growRoomRackKeys } from '../../core/query-keys';
import { growRoomRackEndpoints } from './grow-room-racks.endpoints';
import type {
  GrowRoomRack,
  CreateGrowRoomRackRequest,
  UpdateGrowRoomRackRequest,
} from './grow-room-racks.types';

export function useGrowRoomRacks() {
  return useQuery({
    queryKey: growRoomRackKeys.list,
    queryFn: async () =>
      (
        await api.get<{ grow_room_racks: GrowRoomRack[]; next_page_token: string }>(
          growRoomRackEndpoints.list,
        )
      ).grow_room_racks ?? [],
  });
}

export function useGrowRoomRack(id: string) {
  return useQuery({
    queryKey: growRoomRackKeys.one(id),
    queryFn: () => api.get<GrowRoomRack>(growRoomRackEndpoints.one(id)),
    enabled: Boolean(id),
  });
}

export function useCreateGrowRoomRack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateGrowRoomRackRequest) => api.post<GrowRoomRack>(growRoomRackEndpoints.list, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: growRoomRackKeys.all }),
  });
}

export function useUpdateGrowRoomRack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: UpdateGrowRoomRackRequest & { id: string }) =>
      api.patch<GrowRoomRack>(growRoomRackEndpoints.one(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: growRoomRackKeys.all }),
  });
}

export function useDeleteGrowRoomRack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(growRoomRackEndpoints.one(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: growRoomRackKeys.all }),
  });
}
